import type { OpenSearchResponse } from '../models/search.ts';
import { snakeToCamelCase } from './reporting/string-case-conversion.ts';

/**
 * Normalize object keys from snake_case to camelCase
 * Generic conversion utility (not field-specific)
 */
function normalizeKeys<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    // Use centralized utility for conversion consistency
    const camelCaseKey = snakeToCamelCase(key);
    normalized[camelCaseKey] = value;
  });

  return normalized;
}

/**
 * OpenSearchResponseBuilder - Transforms raw OpenSearch responses into component-friendly formats
 *
 * This class provides utility methods to extract and transform data from OpenSearch responses,
 * making it easier for components to work with search results and aggregations without
 * needing to understand the nested OpenSearch JSON structure.
 *
 * This is a generic data transport conversion that works for any field names.
 */
export class OpenSearchResponseBuilder<T = unknown> {
  constructor(private response: OpenSearchResponse<T>) {}

  /**
   * Extract documents from search hits with automatic key normalization
   *
   * This is a generic data transport conversion that works for any field names.
   */
  getDocuments(): (T & { id: string; _index?: string })[] {
    return (
      this.response.hits?.hits?.map((hit) => {
        // Extract id from _source if it exists, otherwise use _id
        const sourceId =
          hit._source && typeof hit._source === 'object' && 'id' in hit._source
            ? hit._source.id
            : undefined;
        const id = sourceId !== undefined ? String(sourceId) : hit._id;

        // Normalize all keys from snake_case to camelCase
        const normalizedSource = hit._source
          ? normalizeKeys(hit._source as Record<string, unknown>)
          : {};

        return {
          ...normalizedSource,
          id,
          ...(hit._index && { _index: hit._index }),
        } as T & { id: string; _index?: string };
      }) || []
    );
  }

  /**
   * Extract documents from search hits with highlight information and normalized keys
   *
   * This merges the _source document with the highlight data for search result highlighting
   */
  getDocumentsWithHighlights(): (T & {
    id: string;
    _index?: string;
    highlight?: Record<string, string[]>;
  })[] {
    return (
      this.response.hits?.hits?.map((hit) => {
        // Extract id from _source if it exists, otherwise use _id
        const sourceId =
          hit._source && typeof hit._source === 'object' && 'id' in hit._source
            ? hit._source.id
            : undefined;
        const id = sourceId !== undefined ? String(sourceId) : hit._id;

        // Normalize all keys from snake_case to camelCase
        const normalizedSource = hit._source
          ? normalizeKeys(hit._source as Record<string, unknown>)
          : {};

        return {
          ...normalizedSource,
          id,
          ...(hit._index && { _index: hit._index }),
          ...(hit.highlight && { highlight: hit.highlight }),
        } as T & { id: string; _index?: string; highlight?: Record<string, string[]> };
      }) || []
    );
  }

  /**
   * Get total number of matching documents
   */
  getTotalCount(): number {
    return this.response.hits?.total?.value || 0;
  }

  /**
   * Get query execution time in milliseconds
   */
  getExecutionTime(): number {
    return this.response.took || 0;
  }

  /**
   * Check if there are any results
   */
  hasResults(): boolean {
    return this.getTotalCount() > 0;
  }

  /**
   * Extract terms aggregation buckets
   * @param aggregationName - Name of the aggregation
   * @returns Array of buckets with key, count, and any sub-aggregations
   */
  getTermsAggregation(aggregationName: string): TermsBucket[] {
    const agg = (this.response.aggregations as Record<string, unknown>)?.[aggregationName] as {
      buckets?: Array<Record<string, unknown>>;
    };
    if (!agg || !('buckets' in agg)) {
      return [];
    }

    return (agg.buckets || []).map((bucket: Record<string, unknown>) => ({
      key: String(bucket.key),
      count: (bucket.docCount as number) || (bucket.doc_count as number),
      // Include any sub-aggregations (keep the original structure)
      // Note: sub-aggregations are at the bucket level, not under 'aggregations'
      aggregations: Object.keys(bucket).reduce(
        (acc, key) => {
          if (key !== 'key' && key !== 'docCount' && key !== 'doc_count') {
            acc[key] = bucket[key];
          }
          return acc;
        },
        {} as Record<string, unknown>
      ),
    }));
  }

  /**
   * Extract documents from a top_hits aggregation
   * @param aggregationName - Name of the aggregation
   * @returns Array of documents from the top hits
   */
  getTopHitsAggregation(aggregationName: string): T[] {
    const agg = (this.response.aggregations as Record<string, unknown>)?.[aggregationName] as {
      hits?: {
        hits?: Array<Record<string, unknown>>;
      };
    };
    if (!agg || !('hits' in agg)) {
      return [];
    }

    return agg.hits?.hits?.map((hit: Record<string, unknown>) => hit._source as T) || [];
  }

  /**
   * Extract top hits from a specific bucket in a terms aggregation
   * @param termsAggregationName - Name of the parent terms aggregation
   * @param bucketKey - Key of the specific bucket
   * @param topHitsAggregationName - Name of the top hits sub-aggregation
   * @returns Array of documents from the top hits in that bucket
   */
  getTopHitsFromBucket(
    termsAggregationName: string,
    bucketKey: string,
    topHitsAggregationName: string
  ): T[] {
    const buckets = this.getTermsAggregation(termsAggregationName);
    const bucket = buckets.find((b) => b.key === bucketKey);

    if (!bucket?.aggregations?.[topHitsAggregationName]) {
      return [];
    }

    const topHits = bucket.aggregations[topHitsAggregationName] as {
      hits?: {
        hits?: Array<Record<string, unknown>>;
      };
    };
    return topHits.hits?.hits?.map((hit: Record<string, unknown>) => hit._source as T) || [];
  }

  /**
   * Create a paginated result structure similar to legacy search models
   */
  toPaginatedResult(
    options: { includeHighlights?: boolean } = {}
  ): PaginatedSearchResult<T & { _index?: string }> {
    const items = options.includeHighlights
      ? this.getDocumentsWithHighlights()
      : this.getDocuments();

    return {
      items,
      total: this.getTotalCount(),
      took: this.getExecutionTime(),
      isLoading: false,
    };
  }

  /**
   * Static factory method to create a builder from a response
   */
  static from<T>(response: OpenSearchResponse<T>): OpenSearchResponseBuilder<T> {
    return new OpenSearchResponseBuilder(response);
  }
}

// Helper types for the response builder
export interface TermsBucket {
  key: string;
  count: number;
  aggregations?: Record<string, unknown>;
}

export interface PaginatedSearchResult<T> {
  items: T[];
  total: number;
  took: number;
  isLoading?: boolean;
}
